import type { Agenda, AgendaModule } from "@repo/types";
import { Formik } from "formik";
import * as yup from "yup";
import { Input } from "@repo/ui/components/Base/Input";
import { Button } from "@repo/ui/components/Base/Button";
import { TagSelection } from "../tags";
import { AgendaModuleSortableList } from "../dnd";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { useContext, useMemo, useRef } from "react";
import { SearchableAgendaModuleTable } from "../common/SearchableTables";
import { useTrackedNewObjects } from "@/hooks/trackCreatedObjects";
import { useBlockUnload } from "@repo/ui/components/Base/hooks/useBlockUnload";
import { Label } from "@repo/ui/components/Base/Label";
import { AgendaDefaultsContext } from "./AgendaDefaults";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";
import {
  agendaModuleRepo,
  type AgendaModuleDto,
} from "@/repositories/agendaModuleRepo";
import { useAgendaModuleList } from "@/hooks/queryHooks/agendaModuleHooks";
import { useAgendaTemplateList } from "@/hooks/queryHooks/agendaHooks";
import { useActiveTeamId } from "@/hooks/useActiveTeam";
import { agendaRepo } from "@/repositories/agendaRepo";

export interface AgendaFormProps {
  agendaFormData?: Agenda;
  tenantId: string;
  handleCancel: () => void;
  handleComplete: () => void;
  onSubmit?: (agenda: Agenda) => void;
  keepEditing?: (agenda: Agenda) => void;
  viewOnly?: boolean;
  handleGoBack?: () => void;
  agendaName?: string;
  withinDialog?: boolean;
}
export const AgendaForm = ({
  agendaFormData,
  tenantId,
  handleCancel,
  handleComplete,
  onSubmit,
  keepEditing,
  viewOnly,
  handleGoBack,
  agendaName,
  withinDialog,
}: AgendaFormProps) => {
  useBlockUnload(!viewOnly);

  const activeTeamId = useActiveTeamId();

  const { data: agendaModules } = useAgendaModuleList(tenantId, "org");
  const { data: agendaTemplates } = useAgendaTemplateList(
    tenantId,
    activeTeamId || ""
  );
  const formAction = useRef<"add" | "keepEditing">("add");

  const filteredAgendaModules = useMemo(
    () =>
      agendaModules?.filter(
        (agendaModule) => agendaModule.teamId === activeTeamId
      ),
    [agendaModules, activeTeamId]
  );

  const getAgendaModules = (ids: string[]) => {
    if (!agendaModules) return [];

    const agendaModuleMap = agendaModules.reduce(
      (map, agendaModule) => ((map[agendaModule.id] = agendaModule), map),
      Object.create(null) as Record<string, AgendaModuleDto | undefined>
    );

    const responseVals: (AgendaModuleDto & { isEditable: boolean })[] = [];
    for (const id of ids) {
      const agendaModule = agendaModuleMap[id];

      if (agendaModule) {
        responseVals.push({
          ...agendaModule,
          isEditable: !agendaModule.template,
        });
      }
    }
    return responseVals;
  };

  const trackNew = useTrackedNewObjects();

  const validationSchema = yup.object({
    name: yup.string().required("Agenda name is required"),
  });

  const defaults = useContext(AgendaDefaultsContext);

  if (!activeTeamId) {
    // bail if team is org or hasn't loaded yet
    if (activeTeamId === null) return <p>Loading...</p>;
    return null;
  }

  return (
    <Formik
      initialValues={{
        tags: [],
        ...(agendaFormData ??
          agendaRepo.init({
            ...defaults,
            teamId: activeTeamId,
            name: agendaName ?? "",
          })),
      }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        if (!tenantId) {
          console.log("No tenant ID", tenantId);
          return;
        }
        if (values.agendaModules) {
          for (let i = 0; i < values.agendaModules.length; i++) {
            const element = values.agendaModules[i];

            if (element.template) {
              const agendaItemIds: string[] = [];
              for (const agendaItem of element.agendaItems ?? []) {
                const newItem = await agendaItemRepo.create(tenantId, {
                  ...agendaItem,
                  teamId: activeTeamId,
                  id: undefined,
                  template: undefined,
                  templateId: undefined,
                });
                if (newItem) agendaItemIds.push(newItem.id);
                trackNew("agendaItem", newItem); // track created agendaItems
              }

              const newAgendaModule = {
                ...element,
                agendaItemIds,
                agendaItems: null, // populated server-side
                teamId: activeTeamId,
                template: false,
                templateId: element.id,
              } as AgendaModule;

              values.agendaModuleIds[i] = newAgendaModule.id;

              const agendaModule = await agendaModuleRepo.create(
                tenantId,
                newAgendaModule
              );
              values.agendaModules[i] = agendaModule;
              trackNew("agendaModule", agendaModule); // track created agendaModules
            }
          }
        }

        const nonTemplateValues = { ...values, templateId: undefined };
        const updatedAgenda = agendaFormData
          ? await agendaRepo.update(tenantId, values.id, nonTemplateValues)
          : await agendaRepo.create(tenantId, nonTemplateValues);

        if (!agendaFormData) trackNew("agenda", updatedAgenda); // track created agendas

        if (updatedAgenda) onSubmit?.(updatedAgenda);
        handleComplete();
        if (
          updatedAgenda &&
          formAction.current === "keepEditing" &&
          keepEditing
        ) {
          setTimeout(() => keepEditing(updatedAgenda), 300); // wait for close animation to complete so component remounts
        }
      }}
      // enableReinitialize
    >
      {({
        values,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
        errors,
        touched,
        isSubmitting,
        submitForm,
      }) => {
        const onAddModule = (item: AgendaModule) => {
          setFieldValue(
            "agendaModuleIds",
            values.agendaModuleIds
              ? [...values.agendaModuleIds, item.id]
              : [item.id]
          );
          setFieldValue(
            "agendaModules",
            values.agendaModules ? [...values.agendaModules, item] : [item]
          );
        };

        const footer = (
          <>
            <Button
              variant="destructive"
              type="button"
              disabled={isSubmitting}
              onClick={() => {
                viewOnly ? handleGoBack && handleGoBack() : handleCancel();
              }}
            >
              {viewOnly ? "Go Back" : "Cancel"}
            </Button>
            <div className="flex-1" />
            {!viewOnly && (
              <>
                {keepEditing ? (
                  <>
                    <Button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => {
                        formAction.current = "keepEditing";
                        submitForm();
                      }}
                    >
                      Save and Keep Editing
                    </Button>
                    <div className="w-3" />
                  </>
                ) : null}
                <Button type="submit" disabled={isSubmitting}>
                  Save
                </Button>
              </>
            )}
          </>
        );

        // collect a list of used template ids
        const usedAgendaModuleTemplates =
          filteredAgendaModules
            ?.filter((agendaModule) =>
              values.agendaModuleIds?.includes(agendaModule.id)
            )
            .map((agendaModule) => agendaModule.templateId) ?? [];

        const form = (
          <div className="flex flex-col gap-y-4">
            <Input
              disabled={viewOnly || isSubmitting}
              label="*Agenda Title"
              id="name"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.name && touched.name}
              errorMessage={errors.name}
            />
            {/* <TextArea
              disabled={viewOnly || isSubmitting}
              label="Additional Text"
              id="description"
              name="description"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.description && touched.description}
              errorMessage={errors.description}
            /> */}
            {agendaTemplates && (
              <SelectInput
                disabled={viewOnly || isSubmitting}
                label="Agenda Template"
                onValueChange={(value) => {
                  const agendaTemplate = agendaTemplates.find(
                    (template) => template.id === value
                  );
                  if (agendaTemplate) {
                    setFieldValue(
                      "agendaModuleIds",
                      agendaTemplate.agendaModuleIds
                    );
                    setFieldValue(
                      "agendaModules",
                      agendaTemplate.agendaModules
                        ? agendaTemplate.agendaModules.map((agendaModule) => ({
                            ...agendaModule,
                            parentIds: [],
                          }))
                        : []
                    );
                    setFieldValue("templateId", value);
                    setFieldValue("letterHeadId", agendaTemplate.letterHeadId);
                    setFieldValue("letterHead", agendaTemplate.letterHead);
                  }
                }}
                options={
                  agendaTemplates
                    ? agendaTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))
                    : []
                }
                value={values.templateId}
              />
            )}

            <TagSelection
              disabled={viewOnly || isSubmitting}
              label="Tags"
              value={values.tags}
              onChange={(newTags) => setFieldValue("tags", newTags)}
              enableAutocomplete={true}
              useStandardizedTags={true}
            />

            <Label label="Agenda Sections" />
            {agendaModules && (
              <AgendaModuleSortableList
                disabled={viewOnly || isSubmitting}
                items={getAgendaModules(values.agendaModuleIds ?? [])}
                onChange={(items) => {
                  setFieldValue(
                    "agendaModuleIds",
                    items.map((item) => item.id)
                  );
                  setFieldValue("agendaModules", items);
                }}
                editableLabel="Edit Agenda Section"
                notEditableLabel="Save the agenda to edit this template section"
                deleteLabel="Remove Agenda Section"
              />
            )}
            {agendaModules && !viewOnly && (
              <SearchableAgendaModuleTable
                disabled={isSubmitting}
                data={agendaModules?.filter(
                  (agendaModule) =>
                    // filter out sections already on agenda
                    !values.agendaModuleIds?.includes(agendaModule.id) &&
                    // filter out sections in other teams
                    agendaModule.teamId === activeTeamId &&
                    // filter out non-templates
                    agendaModule.template &&
                    // filter out template sections that were already added to the agenda
                    !usedAgendaModuleTemplates.includes(agendaModule.id)
                )}
                onSubmit={onAddModule}
                onAdd={onAddModule}
              />
            )}

            {!withinDialog && (
              <div className="flex flex-row justify-between gap-4">
                {footer}
              </div>
            )}
          </div>
        );

        return (
          <form onSubmit={handleSubmit} className="w-full">
            {withinDialog ? (
              <>
                <DialogBody>{form}</DialogBody>
                <DialogFooter className="justify-between">
                  {footer}
                </DialogFooter>
              </>
            ) : (
              form
            )}
          </form>
        );
      }}
    </Formik>
  );
};
